import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../utils/supabaseClient";
import PasswordField from "../components/PasswordField";

export default function Signup() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const redirectTimer = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const { email, password, name } = form;

      // 1. Sign up using your AuthContext
      const { data, error: signUpError } = await signUp(email.trim(), password);

      if (signUpError) {
        setError(signUpError.message);
        return;
      }

      const user = data?.user;
      if (!user) {
        setError("Signup failed — no user returned.");
        return;
      }

      // 2. Split full name into first + last
      const [firstName, ...rest] = name.trim().split(" ");
      const lastName = rest.join(" ");

      // 3. Insert profile row into Supabase
      const { error: profileError } = await supabase.from("profiles").insert([
        {
          id: user.id,
          email: email.trim(),
          first_name: firstName,
          last_name: lastName,
          is_admin: false,
        },
      ]);

      if (profileError) {
        console.error(profileError);
        setError("Signup succeeded but profile creation failed.");
        return;
      }

      setSuccess("Registration successful! Redirecting to login...");
      redirectTimer.current = setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      console.error("Registration failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-card">
        <h2 className="auth-title">Create an account</h2>
        <p className="auth-sub">Register to start shopping with Aquaviva Foods.</p>

        <form onSubmit={handleSubmit}>
          {error && (
            <p className="ui-alert ui-alert--error" role="alert">
              {error}
            </p>
          )}
          {success && (
            <p className="ui-alert ui-alert--ok" role="status">
              {success}
            </p>
          )}

          <div className="ui-field">
            <label className="ui-label" htmlFor="signup-name">Full name</label>
            <input
              id="signup-name"
              className="ui-input"
              type="text"
              placeholder="Enter your name"
              autoComplete="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div className="ui-field">
            <label className="ui-label" htmlFor="signup-email">Email address</label>
            <input
              id="signup-email"
              className="ui-input"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>

          <PasswordField
            id="signup-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Create a password"
            autoComplete="new-password"
            minLength={6}
            hint="At least 6 characters."
          />

          <button
            type="submit"
            className="ui-btn ui-btn--primary ui-btn--block"
            disabled={loading || Boolean(success)}
          >
            {loading ? "Creating account..." : "Register"}
          </button>
        </form>

        <p className="auth-foot">
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
}